import { useNavigate, useLocation } from "react-router-dom";
import {
  HomeIcon,
  AvatarIcon,
  BarChartIcon,
  ClockIcon,
  GearIcon,
  ExitIcon,
  ActivityLogIcon,
  DashboardIcon,
  EnvelopeClosedIcon,
  PlusIcon,
  HeartIcon,
} from "@radix-ui/react-icons";
import SidebarNavItem from "./SidebarNavItem";
import { useUser } from "../../../contexts/UserContext";
import PATHS from "../../../constants/Paths";
import { isSuperAdmin } from "../../../utils/permissions";

export default function Sidebar() {
  const { logout, user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  // Staff privilege has exactly one source of truth: the `canAccess` array.
  //
  // It must NOT be derived from `user.role`, which is the WORKSPACE role
  // (admin/manager/analyst/viewer) and defaults to "admin" for every account
  // created - so a role-based check silently evaluates true for every customer.
  // That is what previously exposed the staff console to all users.
  const isStaff = isSuperAdmin(user);

  // Prefix matching with a path-segment boundary. The old version used exact
  // match for /scans, so on /scans/new and /scans/:id NO nav item was
  // highlighted at all and the user lost their place in the app.
  const isActive = (path) => {
    if (path === PATHS.DASHBOARD) {
      return location.pathname === PATHS.DASHBOARD;
    }
    return (
      location.pathname === path ||
      location.pathname.startsWith(path + "/")
    );
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(PATHS.LOGIN);
    } catch (error) {
      console.error("Logout error:", error);
      navigate(PATHS.LOGIN);
    }
  };

  return (
    <aside className="flex flex-col justify-between bg-brand-950 text-brand-100 w-64 h-full">
      <div>
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/5">
          <div className="w-8 h-8 rounded-lg bg-brand-600 flex items-center justify-center shrink-0">
            <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <div className="text-white font-semibold text-lg tracking-tight">RepCheck</div>
        </div>

        {/* The product's one primary action, reachable from anywhere. It was
            previously buried behind Dashboard/Scans page buttons only. */}
        <div className="mt-5 px-3">
          <button
            type="button"
            onClick={() => navigate(PATHS.NEW_SCANS)}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-400"
          >
            <PlusIcon className="h-4 w-4" />
            New scan
          </button>
        </div>

        <nav className="mt-4 px-3 space-y-1">
          <SidebarNavItem
            icon={HomeIcon}
            label="Dashboard"
            active={isActive(PATHS.DASHBOARD)}
            onClick={() => navigate(PATHS.DASHBOARD)}
          />
          <SidebarNavItem
            icon={AvatarIcon}
            label="Clients"
            active={isActive(PATHS.CLIENT_LIST)}
            onClick={() => navigate(PATHS.CLIENT_LIST)}
          />
          <SidebarNavItem
            icon={ActivityLogIcon}
            label="Scans"
            active={isActive(PATHS.SCAN_LIST)}
            onClick={() => navigate(PATHS.SCAN_LIST)}
          />
          <SidebarNavItem
            icon={HeartIcon}
            label="Heartbeat"
            active={isActive(PATHS.HEARTBEAT)}
            onClick={() => navigate(PATHS.HEARTBEAT)}
          />
          <SidebarNavItem
            icon={ClockIcon}
            label="Monitoring"
            active={isActive(PATHS.MONITORING)}
            onClick={() => navigate(PATHS.MONITORING)}
          />
          <SidebarNavItem
            icon={BarChartIcon}
            label="Reports"
            active={isActive(PATHS.REPORTS)}
            onClick={() => navigate(PATHS.REPORTS)}
          />
          <SidebarNavItem
            icon={EnvelopeClosedIcon}
            label="Support"
            active={isActive(PATHS.SUPPORT)}
            onClick={() => navigate(PATHS.SUPPORT)}
          />
          <SidebarNavItem
            icon={GearIcon}
            label="Settings"
            active={isActive(PATHS.SETTINGS)}
            onClick={() => navigate(PATHS.SETTINGS)}
          />

          {/* RepCheck staff only. Everything above is the customer's own
              workspace; everything below operates the platform. */}
          {isStaff && (
            <div className="pt-3 mt-3 border-t border-white/5">
              <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-brand-200/50">
                RepCheck staff
              </p>
              <SidebarNavItem
                icon={DashboardIcon}
                label="Admin"
                active={isActive(PATHS.ADMIN)}
                onClick={() => navigate(PATHS.ADMIN)}
              />
              <SidebarNavItem
                icon={DashboardIcon}
                label="Global Admin"
                active={isActive(PATHS.GLOBAL_ADMIN)}
                onClick={() => navigate(PATHS.GLOBAL_ADMIN)}
              />
            </div>
          )}
        </nav>
      </div>

      <div className="mb-4 px-3 pt-3 border-t border-white/5">
        <SidebarNavItem icon={ExitIcon} label="Logout" onClick={handleLogout} />
      </div>
    </aside>
  );
}
